/**
 * Promo code (coupon) rules. Pure, no Vue/Vuex/axios.
 *
 * Ported from the kiosk, adapted to the LocalBee cart item shape:
 * { id, sellPrice, mealsize, garnish, amount, discount, itemGroup }.
 */

export const PROMO_TYPES = {
  BASKET_VALUE: "BASKET_VALUE",
  ITEM_QUANTITY: "ITEM_QUANTITY",
  ITEM_GROUP: "ITEM_GROUP",
  ITEM_BOOKING: "ITEM_BOOKING"
};

export const DISCOUNT_MODES = {
  PERCENTAGE: "PERCENTAGE",
  FIXED_AMOUNT: "FIXED_AMOUNT",
  FIXED_PRICE: "FIXED_PRICE"
};

export const PROMO_CONDITIONS = {
  OK: "OK",
  BASKET_TOO_LOW: "BASKET_TOO_LOW",
  NOT_ENOUGH_ITEMS: "NOT_ENOUGH_ITEMS",
  NO_ELIGIBLE_ITEM: "NO_ELIGIBLE_ITEM"
};

// backend marker, FE keeps detecting promos by promoRules
export const PROMO_EAN_PREFIX = "PROMO$";

export const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

export const isPromoItem = line => !!line && !!line.promoRules;

export const findPromoItem = lines => (lines || []).find(isPromoItem) || null;

export const isBookingPromo = rules =>
  rules && rules.promotionType === PROMO_TYPES.ITEM_BOOKING;

// ITEM_GROUP / ITEM_BOOKING price the lines themselves, the rest deduct from the total.
export const isItemLevelPromo = rules =>
  (rules && rules.promotionType === PROMO_TYPES.ITEM_GROUP) ||
  isBookingPromo(rules);

export const promoBookingItemId = rules =>
  isBookingPromo(rules) && rules.itemIds ? rules.itemIds[0] : undefined;

/** requiredQuantity is a ceiling for a booking promo, not a threshold. */
export const promoBookingCap = rules =>
  Math.max(1, Number(rules && rules.requiredQuantity) || 1);

export const requiredTargetQuantity = rules =>
  isBookingPromo(rules)
    ? 1
    : Math.max(1, Number(rules && rules.requiredQuantity) || 0);

/* ---------- LocalBee line accessors ---------- */

/** Unit price the promo acts on: meal size wins over the item itself. */
export const lineBasePrice = line => {
  if (!line) return 0;
  return Number(line.mealsize ? line.mealsize.sellPrice : line.sellPrice) || 0;
};

export const setLineBasePrice = (line, value) => {
  if (!line) return;
  if (line.mealsize) line.mealsize.sellPrice = value;
  else line.sellPrice = value;
};

export const lineGarnishTotal = line => {
  if (!line || !Array.isArray(line.garnish)) return 0;
  return line.garnish.reduce(
    (sum, garnish) =>
      sum + (Number(garnish.sell_price) || 0) * (Number(garnish.amount) || 0),
    0
  );
};

/**
 * Money value of a line, deposit excluded, mirroring cartTotalSum.
 * The category discount stays applied, a promo reduces the base price on top of it.
 */
export const lineTotal = (line, base = lineBasePrice(line)) => {
  if (!line) return 0;
  const unit = (Number(base) || 0) + lineGarnishTotal(line);
  const afterDiscount = unit - unit * ((Number(line.discount) || 0) / 100);
  return afterDiscount * (Number(line.amount) || 0);
};

export const lineItemGroupId = line =>
  line && line.itemGroup
    ? line.itemGroup.id !== undefined
      ? line.itemGroup.id
      : line.itemGroup
    : undefined;

/* ---------- eligibility and conditions ---------- */

const isRealLine = line => !!line && !isPromoItem(line);

export const realItems = lines => (lines || []).filter(isRealLine);

export function isEligibleLine(line, rules) {
  if (!isRealLine(line)) return false;
  if (rules && rules.promotionType === PROMO_TYPES.BASKET_VALUE) return true;
  if (!rules) return false;
  return (
    (rules.itemIds || []).includes(String(line.id)) ||
    (rules.itemGroupIds || []).includes(String(lineItemGroupId(line)))
  );
}

export const eligibleItems = (lines, rules) =>
  (lines || []).filter(line => isEligibleLine(line, rules));

export const basketSubtotal = lines =>
  round2(realItems(lines).reduce((sum, line) => sum + lineTotal(line), 0));

export const eligibleQuantity = (lines, rules) =>
  eligibleItems(lines, rules).reduce(
    (sum, line) => sum + (Number(line.amount) || 0),
    0
  );

/**
 * Units of itemId already in the cart against the cap of the redeemed booking
 * promo. null for every other item, nothing to enforce.
 */
export function promoBookingLimit(lines, rules, itemId, excludeIndex = null) {
  const bookedId = promoBookingItemId(rules);
  if (
    itemId === undefined ||
    bookedId === undefined ||
    String(bookedId) !== String(itemId)
  )
    return null;

  const used = (lines || []).reduce((sum, line, index) => {
    if (String(line.id) !== String(itemId) || index === excludeIndex) return sum;
    return sum + (Number(line.amount) || 0);
  }, 0);

  return { cap: promoBookingCap(rules), used, code: rules.code };
}

/**
 * promotionType decides first: an ITEM_GROUP promo carries a minBasketValue in
 * the payload that must NOT be enforced.
 */
export function evaluatePromoConditions(lines, rules) {
  if (!rules) return { ok: false, reason: PROMO_CONDITIONS.NO_ELIGIBLE_ITEM };

  if (rules.promotionType === PROMO_TYPES.BASKET_VALUE) {
    const subtotal = basketSubtotal(lines);
    if (rules.minBasketValue > 0 && subtotal < rules.minBasketValue) {
      return {
        ok: false,
        reason: PROMO_CONDITIONS.BASKET_TOO_LOW,
        missingAmount: round2(rules.minBasketValue - subtotal)
      };
    }
    return { ok: true, reason: PROMO_CONDITIONS.OK };
  }

  if (rules.promotionType === PROMO_TYPES.ITEM_QUANTITY) {
    const qty = eligibleQuantity(lines, rules);
    const required = rules.requiredQuantity || 1;
    if (qty < required) {
      return {
        ok: false,
        reason: PROMO_CONDITIONS.NOT_ENOUGH_ITEMS,
        missingQuantity: required - qty
      };
    }
    return { ok: true, reason: PROMO_CONDITIONS.OK };
  }

  if (eligibleItems(lines, rules).length < 1) {
    return { ok: false, reason: PROMO_CONDITIONS.NO_ELIGIBLE_ITEM };
  }
  return { ok: true, reason: PROMO_CONDITIONS.OK };
}

/* ---------- pricing ---------- */

/** Order level reduction, never bigger than the cart it discounts. */
export function computePromoDiscount(base, rules) {
  if (!rules || base <= 0) return 0;
  const raw =
    rules.discountMode === DISCOUNT_MODES.PERCENTAGE
      ? base * (rules.discountValue / 100)
      : rules.discountValue;
  return round2(Math.min(Math.max(raw, 0), base));
}

/** Item level promos keep the promo line at 0, the reduction sits on the lines. */
export function promoLinePrice(lines, rules) {
  if (!rules || isItemLevelPromo(rules)) return 0;
  const discount = computePromoDiscount(basketSubtotal(lines), rules);
  return discount > 0 ? -discount : 0;
}

/**
 * Discounted unit price for an eligible line. Paid garnish still stacks on top.
 * ponytail: a promo percentage stacks on the category discount instead of
 * replacing it, switch to Math.max of the two if the business wants that.
 */
export function linePromoBasePrice(line, rules, originalBase) {
  const base =
    originalBase !== undefined ? Number(originalBase) || 0 : lineBasePrice(line);

  if (rules.discountMode === DISCOUNT_MODES.FIXED_PRICE) {
    return Math.max(0, round2(rules.discountValue));
  }
  if (rules.discountMode === DISCOUNT_MODES.PERCENTAGE) {
    return round2(base * ((100 - rules.discountValue) / 100));
  }
  return Math.max(0, round2(base - rules.discountValue));
}

/** True while an item level promo is pricing this line. */
export const isPromoDiscounted = line =>
  !!line && line.promoOriginalBase !== undefined;

/** What the line cost before the promo touched it. */
export const promoOriginalTotal = line =>
  isPromoDiscounted(line) ? round2(lineTotal(line, line.promoOriginalBase)) : 0;

export const promoOriginalUnitPrice = line =>
  isPromoDiscounted(line) ? round2(Number(line.promoOriginalBase) || 0) : 0;

/** Whole percent saved on this line, for the badge. */
export function promoDiscountPercent(line) {
  if (!isPromoDiscounted(line)) return 0;

  const before = lineTotal(line, line.promoOriginalBase);
  if (before <= 0) return 0;

  return Math.round(((before - lineTotal(line)) / before) * 100);
}

/** Total saved across the lines an item level promo actually touched. */
export function itemPromoSavings(lines) {
  return round2(
    (lines || []).reduce((sum, line) => {
      if (!line || line.promoOriginalBase === undefined) return sum;
      return sum + (lineTotal(line, line.promoOriginalBase) - lineTotal(line));
    }, 0)
  );
}

/* ---------- cart line ---------- */

export function buildPromoCartItem(rules, price = 0) {
  return {
    id: rules.code,
    ean: PROMO_EAN_PREFIX + rules.code,
    name: rules.name || rules.code,
    sellPrice: price,
    mealsize: null,
    garnish: [],
    freetext: "",
    amount: 1,
    deliverPrice: price,
    takeAwayPrice: price,
    discount: 0,
    itemGroup: null,
    deposit: null,
    depositgroupID: null,
    canBeSoldOncePerOrder: false,
    promoRules: rules,
    promoSavings: 0
  };
}

/** Drops the hint only target names, the order endpoint has no use for them. */
export function promoItemForOrder(line) {
  if (!isPromoItem(line)) return line;
  const promoRules = { ...line.promoRules };
  delete promoRules.itemNames;
  delete promoRules.itemGroupNames;
  return { ...line, promoRules };
}

/**
 * The cart as the order endpoint has to receive it.
 *
 * An item level promo re-prices its lines in the cart, but the order endpoint
 * prices every position from the item table and drops whatever the client sent,
 * so that discount would be lost. Book it the way a BASKET_VALUE promo is
 * booked instead: lines at their catalogue price and the whole discount on the
 * promo line. The cart keeps its own per row prices, only the payload changes.
 */
export function cartForOrder(lines) {
  const payload = JSON.parse(JSON.stringify(lines || []));
  const promo = payload.find(isPromoItem) || null;
  const savings = itemPromoSavings(payload);

  payload.forEach(line => {
    if (!line || line.promoOriginalBase === undefined) return;
    setLineBasePrice(line, line.promoOriginalBase);
    delete line.promoOriginalBase;
  });

  // promoSavings means "already taken off the item prices", which is no longer
  // the case once the discount sits on the promo line: keep them from being
  // counted twice
  if (promo && savings > 0) {
    promo.sellPrice = -savings;
    promo.promoSavings = 0;
  }

  return payload;
}

/* ---------- response mapping ---------- */

/** The backend flags success inconsistently across the promo endpoints. */
export function isPromoSuccess(data) {
  if (!data) return false;
  const flag = data.success !== undefined ? data.success : data.status;
  if (flag === true || flag === 1 || flag === "1") return true;
  if (typeof flag === "string") return flag.toUpperCase() === "SUCCESS";
  return false;
}

export const promoMessage = data => (data && (data.msg || data.message)) || null;

// Targets arrive as [{ id, name }], older payloads as bare ids.
const toTargets = list =>
  (Array.isArray(list) ? list : []).map(entry =>
    entry && typeof entry === "object"
      ? { id: String(entry.id), name: entry.name || "" }
      : { id: String(entry), name: "" }
  );

export function promoRulesFromResponse(promotion, code) {
  if (!promotion) return null;
  const itemTargets = toTargets(promotion.itemIds);
  const groupTargets = toTargets(promotion.itemGroupIds);
  return {
    code: String(promotion.code || code || "")
      .trim()
      .toUpperCase(),
    name: promotion.name || promotion.code || code,
    description: promotion.description || "",
    promotionType: promotion.promotionType,
    discountMode: promotion.discountMode,
    discountValue: Number(promotion.discountValue) || 0,
    minBasketValue: Number(promotion.minBasketValue) || 0,
    requiredQuantity: Number(promotion.requiredQuantity) || 0,
    itemGroupIds: groupTargets.map(t => t.id),
    itemIds: itemTargets.map(t => t.id),
    itemNames: itemTargets.map(t => t.name).filter(Boolean),
    itemGroupNames: groupTargets.map(t => t.name).filter(Boolean)
  };
}

/** Promo target items out of a flat item array or an item group shaped array. */
export function promoTargetsFromResponse(data, rules) {
  const entries =
    [
      data && data.items,
      data && data.promotion && data.promotion.items,
      data && data.promotion && data.promotion.itemGroups
    ].find(entry => Array.isArray(entry) && entry.length) || [];

  const wanted = new Set(((rules && rules.itemIds) || []).map(String));

  return entries.flatMap(entry =>
    Array.isArray(entry.items)
      ? entry.items
          .filter(item => wanted.has(String(item.id)))
          .map(item => ({ ...item, itemGroup: entry }))
      : wanted.has(String(entry.id))
      ? [{ ...entry }]
      : []
  );
}
